import { authenticatedFetch } from "./http";
import type { PutawayTask } from "./models";
import type { PutawayProblemReason, PutawayProblemResult } from "./putaway";

export interface WarehouseNode {
  id: string;
  parentId: string | null;
  warehouseId: string | null;
  type: "SITE" | "WAREHOUSE" | "STORAGE_AREA" | "STORAGE_SECTION" | "STORAGE_BIN";
  code: string;
  displayName: string;
  status: string;
  attributes: string;
  customData: string;
  version: number;
}

export interface CreateWarehouseNode {
  organizationId: string;
  parentId: string | null;
  type: WarehouseNode["type"];
  code: string;
  displayName: string;
  classification?: string;
  travelSequence?: number;
  aisleCode?: string;
  bayCode?: string;
  levelCode?: string;
  positionCode?: string;
  usableLengthMm?: number;
  usableWidthMm?: number;
  usableHeightMm?: number;
  openingWidthMm?: number;
  openingHeightMm?: number;
  maxWeightKg?: number;
  maxHandlingUnits?: number;
  customData: Record<string, unknown>;
}

export function loadWarehouseStructure(): Promise<WarehouseNode[]> {
  return authenticatedFetch<WarehouseNode[]>("/api/v1/warehouse-structure");
}

export function createWarehouseNode(request: CreateWarehouseNode): Promise<WarehouseNode> {
  return authenticatedFetch<WarehouseNode>("/api/v1/warehouse-structure/nodes", {
    method: "POST",
    body: JSON.stringify(request)
  });
}

export interface ProcessPointMasterData {
  id: string;
  warehouseId: string;
  warehouseCode: string;
  code: string;
  displayName: string;
  type: string;
  status: string;
  linkedStorageBinId: string | null;
  linkedStorageBinCode: string | null;
  travelSequence: number | null;
  priority: number;
  capacityHu: number | null;
  putawaySourceEnabled: boolean;
  defaultForReceiving: boolean;
  defaultForQuality: boolean;
  defaultForRepacking: boolean;
  capabilities: string[];
  version: number;
}

export interface ProcessPointMutation {
  warehouseId?: string;
  code?: string;
  displayName: string;
  type: string;
  linkedStorageBinId: string | null;
  travelSequence: number | null;
  priority: number;
  capacityHu: number | null;
  putawaySourceEnabled: boolean;
  defaultForReceiving: boolean;
  defaultForQuality: boolean;
  defaultForRepacking: boolean;
  capabilities: string[];
  version?: number;
  reason?: string;
}

export interface ProcessPointImpact {
  processPointId: string;
  handlingUnitCount: number;
  openTaskCount: number;
  activeDestinationCount: number;
  historicalReferenceCount: number;
  archiveAllowed: boolean;
  permanentDeleteAllowed: boolean;
  blockers: string[];
}

export interface ProcessPointAudit {
  id: string;
  action: string;
  reason: string;
  changedAt: string;
  changedByDisplayName: string;
}

export function loadProcessPointMasterData(): Promise<ProcessPointMasterData[]> {
  return authenticatedFetch<ProcessPointMasterData[]>("/api/v1/process-points");
}

export function createProcessPoint(
  request: ProcessPointMutation
): Promise<ProcessPointMasterData> {
  return authenticatedFetch<ProcessPointMasterData>("/api/v1/process-points", {
    method: "POST", body: JSON.stringify(request)
  });
}

export function updateProcessPoint(
  id: string, request: ProcessPointMutation
): Promise<ProcessPointMasterData> {
  return authenticatedFetch<ProcessPointMasterData>(`/api/v1/process-points/${id}`, {
    method: "PATCH", body: JSON.stringify(request)
  });
}

export function loadProcessPointImpact(id: string): Promise<ProcessPointImpact> {
  return authenticatedFetch<ProcessPointImpact>(
    `/api/v1/process-points/${id}/impact`
  );
}

export function loadProcessPointAudit(id: string): Promise<ProcessPointAudit[]> {
  return authenticatedFetch<ProcessPointAudit[]>(
    `/api/v1/process-points/${id}/audit`
  );
}

export function archiveProcessPoint(
  id: string, version: number, reason: string
): Promise<ProcessPointMasterData> {
  return authenticatedFetch<ProcessPointMasterData>(`/api/v1/process-points/${id}`, {
    method: "DELETE", body: JSON.stringify({ version, reason })
  });
}

export function deleteProcessPointPermanently(
  id: string, version: number, reason: string
): Promise<void> {
  return authenticatedFetch<void>(`/api/v1/process-points/${id}/permanent`, {
    method: "DELETE", body: JSON.stringify({ version, reason })
  });
}

export interface WarehouseNodeImpact {
  nodeId: string;
  nodeType: WarehouseNode["type"];
  childCount: number;
  handlingUnitCount: number;
  openTaskCount: number;
  activeReservationCount: number;
  activeRuleCount: number;
  processPointCount: number;
  historicalReferenceCount: number;
  archiveAllowed: boolean;
  permanentDeleteAllowed: boolean;
  blockers: string[];
}

export interface UpdateWarehouseNode {
  displayName: string;
  classification?: string;
  travelSequence?: number;
  aisleCode?: string;
  bayCode?: string;
  levelCode?: string;
  positionCode?: string;
  usableLengthMm?: number;
  usableWidthMm?: number;
  usableHeightMm?: number;
  openingWidthMm?: number;
  openingHeightMm?: number;
  maxWeightKg?: number;
  maxHandlingUnits?: number;
  version: number;
  reason: string;
}

export function loadWarehouseNodeImpact(
  nodeId: string
): Promise<WarehouseNodeImpact> {
  return authenticatedFetch<WarehouseNodeImpact>(
    `/api/v1/warehouse-structure/nodes/${nodeId}/impact`
  );
}

export function updateWarehouseNode(
  nodeId: string, request: UpdateWarehouseNode
): Promise<WarehouseNode> {
  return authenticatedFetch<WarehouseNode>(
    `/api/v1/warehouse-structure/nodes/${nodeId}`,
    { method: "PATCH", body: JSON.stringify(request) }
  );
}

export function archiveWarehouseNode(
  nodeId: string, version: number, reason: string
): Promise<WarehouseNode> {
  return authenticatedFetch<WarehouseNode>(
    `/api/v1/warehouse-structure/nodes/${nodeId}`,
    { method: "DELETE", body: JSON.stringify({ version, reason }) }
  );
}

export function deleteWarehouseNodePermanently(
  nodeId: string, version: number, reason: string
): Promise<void> {
  return authenticatedFetch<void>(
    `/api/v1/warehouse-structure/nodes/${nodeId}/permanent`,
    { method: "DELETE", body: JSON.stringify({ version, reason }) }
  );
}

export interface BinRange {
  prefix: string;
  from: number;
  to: number;
  padding: number;
}

export interface BinGenerationRequest {
  organizationId: string;
  parentId: string;
  codePattern: string;
  aisle: BinRange;
  bay: BinRange;
  level: BinRange;
  position: BinRange;
  binType: string;
  usableLengthMm?: number;
  usableWidthMm?: number;
  usableHeightMm?: number;
  openingWidthMm?: number;
  openingHeightMm?: number;
  maxWeightKg?: number;
  maxHandlingUnits?: number;
  travelSequenceStart?: number;
}

export interface BinGenerationCandidate {
  code: string;
  aisle: string;
  bay: string;
  level: string;
  position: string;
  travelSequence: number;
  status: "NEW" | "EXISTS" | "CREATED";
}

export interface BinGenerationResult {
  totalCount: number;
  newCount: number;
  duplicateCount: number;
  candidates: BinGenerationCandidate[];
}

export interface BinDetail {
  id: string;
  code: string;
  displayName: string;
  status: "ACTIVE" | "BLOCKED_INBOUND" | "BLOCKED_OUTBOUND" | "BLOCKED_ALL" | "INACTIVE";
  statusReasonCode: string | null;
  statusComment: string | null;
  statusChangedAt: string | null;
  version: number;
  warehouseId: string;
  warehouseCode: string;
  storageAreaId: string;
  storageAreaCode: string;
  storageSectionId: string | null;
  storageSectionCode: string | null;
  aisle: string | null;
  bay: string | null;
  level: string | null;
  position: string | null;
  travelSequence: number | null;
  usableLengthMm: number | null;
  usableWidthMm: number | null;
  usableHeightMm: number | null;
  openingWidthMm: number | null;
  openingHeightMm: number | null;
  maxWeightKg: number | null;
  maxVolumeMm3: number | null;
  maxHandlingUnits: number | null;
  occupiedWeightKg: number;
  occupiedVolumeMm3: number;
  occupiedHandlingUnits: number;
  reservedWeightKg: number;
  reservedVolumeMm3: number;
  reservedHandlingUnits: number;
  putawayAllowed: boolean;
  pickingAllowed: boolean;
}

export interface BinAuditEntry {
  id: string;
  action: string;
  previousStatus: string | null;
  newStatus: string | null;
  reasonCode: string | null;
  comment: string | null;
  changedAt: string;
  changedBy: string;
  correlationId: string;
}

export interface ChangeBinStatus {
  status: BinDetail["status"];
  reasonCode: string;
  comment: string;
  version: number;
}

export function loadBin(binId: string): Promise<BinDetail> {
  return authenticatedFetch<BinDetail>(`/api/v1/warehouse-structure/bins/${binId}`);
}

export function loadBinAudit(binId: string): Promise<BinAuditEntry[]> {
  return authenticatedFetch<BinAuditEntry[]>(
    `/api/v1/warehouse-structure/bins/${binId}/audit`
  );
}

export function changeBinStatus(
  binId: string, request: ChangeBinStatus
): Promise<BinDetail> {
  return authenticatedFetch<BinDetail>(
    `/api/v1/warehouse-structure/bins/${binId}/status`,
    { method: "PATCH", body: JSON.stringify(request) }
  );
}

export function reportPutawayProblem(
  task: PutawayTask,
  reasonCode: PutawayProblemReason,
  comment: string
): Promise<PutawayProblemResult> {
  return authenticatedFetch<PutawayProblemResult>(
    `/api/v1/putaway/tasks/${task.id}/report-problem`,
    {
      method: "POST",
      body: JSON.stringify({ reasonCode, comment, version: task.version })
    }
  );
}

export function previewBinGeneration(
  request: BinGenerationRequest
): Promise<BinGenerationResult> {
  return authenticatedFetch<BinGenerationResult>(
    "/api/v1/warehouse-structure/bins/generation-preview",
    { method: "POST", body: JSON.stringify(request) }
  );
}

export function generateBins(
  request: BinGenerationRequest
): Promise<BinGenerationResult> {
  return authenticatedFetch<BinGenerationResult>(
    "/api/v1/warehouse-structure/bins/generate",
    { method: "POST", body: JSON.stringify(request) }
  );
}
